import React, { useEffect, useState } from 'react';
import { navigate, withPrefix } from 'gatsby';
import translatePage from '../i18n/translatePage';
import useTranslation from '../hooks/useTranslation';
import styled from 'styled-components';
import variables from '@ingka/variables/design-tokens';
import Loading, { LoadingBall } from '@ingka/loading';
import { useCart } from '../contexts/Cart';
import Form from '../components/Form/Form';
import useUser from '../hooks/useUser';
import { useSubmitOrder } from '../hooks/useOrder';
import SiteMetadata from '../components/SiteMetadata';
import TMarkUp from '../components/TMarkUp';
import ShippingInfoBox from '../components/ShippingInfoBox';
import Breadcrumbs from '../components/Breadcrumbs';
import AdditionalInformationLabel from '../components/AdditionalInformatonLabel';
import LoginPromptCard from '../components/LoginPromptCard';
import OrderItemsSummary from '../components/OrderItemsSummary';
import { getLoginLink } from '../helpers/loginLinkHelper';

/* global CSS_CONFIG */

const FlexContainer = styled.div`
  display: flex;
  width: 100%;
  margin: 30px 0;
  flex-direction: column;
  flex-wrap: wrap;
  overflow-wrap: break-word;
  word-break: break-word;
  ${`@media (min-width: ${variables.breakpointM}) {
    flex-direction: row;
    margin: 90px 0;
    gap: 2rem;
  }`}
`;

const Box = styled.div`
  width: 100%;
  min-width: 0;
  box-sizing: border-box;
  ${`@media (min-width: ${variables.breakpointM}) {
    width: calc((100% - 2rem) / 2);
    box-sizing: border-box;
  }`}
`;

const Wrapper = styled.div`
  ${`@media (min-width: ${variables.breakpointM}) {
    padding-right: 2rem;
    box-sizing: border-box;
    display: flex;
    flex-direction: column;
    height: 100%;
  }`}
`;

const FormBox = styled(Box)`
  ${`@media (min-width: ${variables.breakpointM}) {
    padding-top: 3rem;
  }`}
`;

const DesktopOnly = styled.div`
  display: none;
  ${`@media (min-width: ${variables.breakpointM}) {
    display: block;
    width: 100%;
    max-width: 640px;
    box-sizing: border-box;
    margin-top: auto;
    padding-top: 2rem;
  }`}
`;

const MobileOnly = styled.div`
  width: 100%;
  margin: 1rem 0;
  ${`@media (min-width: ${variables.breakpointM}) {
    display: none;
  }`}
`;

const CustomerInformation = (): JSX.Element | null => {
  const { user, isLoading } = useUser();
  const { t } = useTranslation();

  const crumbs = [
    { url: withPrefix('/'), text: t('seoContent.page.search') },
    {
      url: '/customer-information',
      text: t('seoContent.page.customerInformation'),
    },
  ];
  const customerInfoLogin = CSS_CONFIG.CUSTOMERINFOLOGIN;
  const title = t('seoContent.page.customerInformation');
  const [{ isLoading: isSubmitLoading, isSuccess }, { onSubmitOrder }] =
    useSubmitOrder();
  const [loginUrl, setLoginUrl] = useState<string>();
  const cartContext = useCart();

  const items = cartContext.getItems();
  const additionalTopLabelText = t('orderForm.extraAdditionalInformationLabel');
  const { additionalInformationLabel } = CSS_CONFIG.FORMSETTINGS;

  useEffect(() => {
    if (window && window.location) {
      setLoginUrl(getLoginLink(window.location.toString(), CSS_CONFIG));
    }
  }, []);

  useEffect(() => {
    if (isSuccess) {
      navigate('/summary');
    }
  }, [isSuccess]);

  useEffect(() => {
    if (!cartContext.state.items.size && !cartContext.state.isLoading) {
      navigate('/');
    }
  }, [cartContext.state]);

  if (!items.length) {
    return null;
  }

  if (isLoading) {
    return (
      <Loading
        text={t('loading', {
          loading: t('orderForm.fieldSet.contactInformation'),
        })}
      >
        <LoadingBall />
      </Loading>
    );
  }

  const shouldShowLoginPrompt =
    !!customerInfoLogin && !user.isAuthenticated && !!loginUrl;

  return (
    <>
      <SiteMetadata pageTitle={title} />
      <Breadcrumbs crumbs={crumbs} />
      <FlexContainer>
        <Box role="region" aria-label={t('customerPage.title')}>
          <Wrapper>
            <h1>
              <TMarkUp value={t('customerPage.title')} />
            </h1>
            {shouldShowLoginPrompt && loginUrl && (
              <LoginPromptCard linkTo={loginUrl} />
            )}
            <DesktopOnly>
              <OrderItemsSummary items={items} />
              {CSS_CONFIG.SHIPPINGINFOBOX && <ShippingInfoBox />}
            </DesktopOnly>
          </Wrapper>
        </Box>

        <FormBox
          role="region"
          aria-label={t('orderForm.fieldSet.contactInformation')}
        >
          {additionalInformationLabel && (
            <AdditionalInformationLabel text={additionalTopLabelText} />
          )}
          <Form
            onSubmit={onSubmitOrder}
            isAuthenticated={user.isAuthenticated}
            values={user.fieldValues}
            isLoading={isSubmitLoading || isSuccess}
            backPath="/"
            slotBeforeTerms={
              <MobileOnly>
                <OrderItemsSummary items={items} />
                {CSS_CONFIG.SHIPPINGINFOBOX && <ShippingInfoBox />}
              </MobileOnly>
            }
          />
        </FormBox>
      </FlexContainer>
    </>
  );
};
export default translatePage()(CustomerInformation);
